import React, { useEffect, useState } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { dossierUrl } from "../mock/mock";

const Header = () => {
  const { t, lang, setLang, supported } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const navItems = [
    { label: t("nav.profile"), href: "#profile" },
    { label: t("nav.video"), href: "#video" },
    { label: t("nav.stats"), href: "#stats" },
    { label: t("nav.tactical"), href: "#tactical" },
    { label: t("nav.career"), href: "#career" },
    { label: t("nav.achievements"), href: "#achievements" },
    { label: t("nav.gallery"), href: "#gallery" },
    { label: t("nav.contact"), href: "#contact" }
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu open; unmount contents when closed.
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-marseille/95 border-b border-white/15" : "bg-marseille/0"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex items-center justify-between h-16 md:h-20">
        {/* Horizontal logo — min 200px wide with safety area, white, sharp */}
        <a
          href="#top"
          className="flex items-center shrink-0 py-3"
          aria-label="Emily Burns · Home"
          data-testid="header-logo-link"
        >
          <img
            src="/brand/logo-horizontal.webp"
            alt="Emily Burns"
            width="220"
            height="44"
            className="w-[200px] md:w-[220px] h-auto block select-none"
            style={{ filter: "brightness(0) invert(1)" }}
            fetchPriority="high"
            data-testid="header-logo-img"
          />
        </a>

        {/* Nav inline only from 1440px — avoids overflow of dossier button and nav collision below that */}
        <nav className="hidden min-[1440px]:flex items-center gap-6 xl:gap-7 pl-8 mr-6">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="cta-underline text-[11.5px] uppercase tracking-[0.22em] text-white/80 hover:text-white font-medium transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 px-3 py-2 border border-white/35 text-white hover:bg-white hover:text-marseille text-[11px] uppercase tracking-[0.22em] font-medium transition-colors"
              aria-label="Language"
              aria-expanded={langOpen}
            >
              <Globe size={13} />
              {lang.toUpperCase()}
            </button>
            {langOpen && (
              <div
                className="absolute right-0 top-full mt-2 bg-marseille-dark border border-white/20 min-w-[120px] z-50"
                onMouseLeave={() => setLangOpen(false)}
              >
                {supported.map((code) => (
                  <button
                    key={code}
                    onClick={() => {
                      setLang(code);
                      setLangOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-medium transition-colors ${
                      lang === code ? "bg-white text-marseille" : "text-white/85 hover:bg-white/10"
                    }`}
                  >
                    {code === "en" ? "English" : code === "fr" ? "Français" : "Español"}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={dossierUrl}
            target="_blank"
            rel="noreferrer"
            className="hidden min-[1440px]:inline-flex items-center whitespace-nowrap px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-semibold bg-white text-marseille hover:bg-caliza transition-colors"
            data-testid="header-dossier-btn"
          >
            {t("nav.downloadDossier")}
          </a>

          <button
            onClick={() => setOpen(!open)}
            className="min-[1440px]:hidden text-white p-1"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu — contents mounted only when open */}
      {open && (
        <div className="min-[1440px]:hidden bg-marseille-dark border-t border-white/15">
          <nav className="flex flex-col px-6 py-6 gap-5">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.22em] text-white/90 font-medium hover:text-white"
              >
                {item.label}
              </a>
            ))}
            <a
              href={dossierUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              className="mt-2 text-center py-3 text-[11px] uppercase tracking-[0.22em] font-semibold bg-white text-marseille"
            >
              {t("nav.downloadDossier")}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
